import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";

interface HistoryUBTList {
  [key: string]: any;
}

interface HistoryUBTState {
  historyUBTList: HistoryUBTList[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
}

const initialState: HistoryUBTState = {
  historyUBTList: [],
  loading: false,
  error: null,
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  totalPages: 0,
};

export const fetchHistoryUBTList = createAsyncThunk(
  "ubtPortal/fetchHistoryUBTList",
  async (
    {
      page,
      perPage,
      sortBy,
      sortOrder,
      teamId,
    }: {
      page: number;
      perPage: number;
      sortBy?: string;
      sortOrder?: string;
      teamId?: number | string | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        params: {
          page,
          per_page: perPage,
          ...(sortBy && { sortColumn: sortBy }),
          ...(sortOrder && { sortOrder: sortOrder }),
        },
      };

      const url =
        typeof endpoints.ubt.history.historyUBTList === "function"
          ? endpoints.ubt.history.historyUBTList(teamId ?? undefined)
          : endpoints.ubt.history.historyUBTList;
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
    if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }

      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.detail ??
        error?.response?.statusText ??
        error?.message ??
        "Failed to fetch history UBT List";

      return rejectWithValue(errorMessage);
    }
  },
);

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    resetHistoryUBTState: (state) => {
      state.historyUBTList = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHistoryUBTList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchHistoryUBTList.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      // Handle API response with meta object and items array (prioritize this format)
      if (payload?.meta && payload?.items && Array.isArray(payload.items)) {
        state.historyUBTList = payload.items;
        state.totalItems = payload.meta.total ?? 0;
        state.totalPages = payload.meta.totalPages ?? 1;
        state.currentPage = payload.meta.pageNumber ?? 1;
        state.perPage = payload.meta.pageSize ?? 10;
      } else {
        state.historyUBTList = payload ? [payload] : [];
      }
    });
    builder.addCase(fetchHistoryUBTList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { resetHistoryUBTState, setCurrentPage } = historySlice.actions;
export default historySlice.reducer;
